(ns app.front.view.group.admin.target-time-config
  "Admin config page editor for configuring various target times (unit and value) such as how many business days should
  be the application be deemed as complete before"
  (:require [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.target-time.model :as target-time-model]
            [app.front.ui :as ui]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.group.admin.common :as acommon]
            [app.front.view.group.admin.subs :as gas]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub]]
            [core.front.feature-flags :as ff]))


(defn target-time-unit-select
  ([tr *group-config unit-attr label id disabled? required?]
   (target-time-unit-select tr *group-config unit-attr label id disabled? required? target-time-model/target-time-unit-choice-days))
  ([tr *group-config unit-attr label id disabled? required? values]
   (let [target-time-unit (get @*group-config unit-attr)]
     [ui-select/select
      {:id           id
       :label-text   label
       :val->str     #(tr [%])
       :required     required?
       :value        target-time-unit
       :disabled     disabled?
       :allow-empty? false
       :values       values
       :on-change    #(swap! *group-config assoc unit-attr %)}])))


(defn- target-time-fields
  "Fields for handling a single target time (both unit and value)"
  [{:keys [tr *group-config type-str]}]
  (let [unit-attr         (keyword "group.config" (str "target-time-" type-str "-unit"))
        value-attr        (keyword "group.config" (str "target-time-" type-str "-value"))
        label-key         (keyword "target-time" type-str)
        target-time-value (get @*group-config value-attr)]
    [:div.flex.flex-wrap.justify-start
     [ui/number-input
      {:id           (str "target-time-value-" type-str)
       :label-text   (tr [label-key])
       :on-change    #(let [val (-> % futil/tval futil/parse-int)]
                        (when (nil? val)
                          (swap! *group-config assoc unit-attr nil)) ; "assoc nil" instead of "dissoc" to tell backend to retract
                        (swap! *group-config assoc value-attr val))
       :value        target-time-value
       :invalid      (neg-int? target-time-value)
       :invalid-text (tr [:validate/negative-number])}]
     [target-time-unit-select tr *group-config unit-attr (tr [:target-time.unit/label]) (str "target-time-unit-" type-str)
      (not target-time-value) (some? target-time-value)]]))


(defn target-time-editor [{:keys [tr *group-config parent-config target permit-type] :as opts}]
  (when (<sub [::gas/has-feature? :group.feature/target-processing-times])
    (let [not-building-permit?  (not (ptc-model/building-permit-types permit-type))
          planning-permit?      (= permit-type :permit-type/P)
          reviews-in-submitted? (and (<sub [::ff/enabled? :feature-flag.id/reviews-in-submitted])
                                     (<sub [::gas/has-feature? :group.feature/reviews-in-submitted]))
          completed-str         "completed"
          decided-str           "decided"
          appealed-str          "appealed"]

      (cc/form-group
        {:legend-text (tr [:group-admin/target-times])}
        [:div.config-row
         [acommon/checkbox opts :group.config/target-times-visible-for-authority-only?]]

        (when (ptc-model/building-permit-types permit-type)
          [:<>
           [:div.config-row
            [ui-select/select
             {:id         (str "calculation-starting-day-" target)
              :label-text (tr [:target-time.starting-day/label])
              :values     target-time-model/calculation-starting-day-choice
              :value      (get @*group-config :group.config/target-times-calculation-starting-day)
              :val->str   #(tr [%])
              :on-change  #(swap! *group-config assoc :group.config/target-times-calculation-starting-day %)}]]

           [:div.config-row
            [ui/time-input
             {:id             (str "end-time-of-business-day-" target)
              :label-text     (let [default-time   (if-let [parent-value (:group.config/target-times-business-day-end-time
                                                                           parent-config)]
                                                     ;; TODO: p/deep-merge concatenates vectors. Change that for tuple types
                                                     (subvec parent-value (- (count parent-value) 2))
                                                     target-time-model/business-day-end-time-default)
                                    formatted-time (date-util/format-time default-time)]
                                (tr [:target-time.business-day/end-time] [formatted-time]))
              :on-change      (fn [new-end-time]
                                (swap! *group-config assoc :group.config/target-times-business-day-end-time new-end-time))
              :value          (get @*group-config :group.config/target-times-business-day-end-time)
              :show-clear-btn true
              :clear-btn-text (tr [:clear])}]]])

        [:div.config-row
         (cc/checkbox {:id         (str "target-time-manual-inputs-" target)
                       :label-text (tr [:group.config/target-time-manual-inputs?])
                       :checked    (or (get parent-config :group.config/target-time-manual-inputs?)
                                       (get @*group-config :group.config/target-time-manual-inputs?)
                                       false)
                       :disabled   (boolean (get parent-config :group.config/target-time-manual-inputs?))
                       :on-change  #(do (swap! *group-config assoc :group.config/target-time-manual-inputs? %)
                                        (when %
                                          (swap! *group-config assoc :group.config/target-time-completed-value nil)
                                          (swap! *group-config assoc :group.config/target-time-decided-value nil)
                                          (when not-building-permit?
                                            (swap! *group-config assoc :group.config/target-time-appealed-value nil))))})]

        (when (and planning-permit? reviews-in-submitted?)
          [:div.config-row
           (cc/checkbox {:id         (str "reset-target-times-on-resubmission-" target)
                         :label-text (tr [:group.config/reset-target-times-on-resubmission?])
                         :checked    (or (get parent-config :group.config/reset-target-times-on-resubmission?)
                                         (get @*group-config :group.config/reset-target-times-on-resubmission?)
                                         false)
                         :disabled   (boolean (get parent-config :group.config/reset-target-times-on-resubmission?))
                         :on-change  #(swap! *group-config assoc :group.config/reset-target-times-on-resubmission? %)})])

        (if (or (get @*group-config :group.config/target-time-manual-inputs?) (get parent-config :group.config/target-time-manual-inputs?))
          [:div
           [target-time-unit-select tr *group-config :group.config/target-time-completed-unit
            (tr [:target-time/calculations-completed]) (str completed-str "-manual-type-select") false true]
           [target-time-unit-select tr *group-config :group.config/target-time-decided-unit
            (tr [:target-time/calculations-decided]) (str decided-str "-manual-type-select") false true]
           (when not-building-permit?
             [target-time-unit-select tr *group-config :group.config/target-time-appealed-unit
              (tr [:target-time/calculations-appealed]) (str appealed-str "-manual-type-select") false true])]
          [:div
           [target-time-fields (assoc opts :type-str completed-str)]
           [target-time-fields (assoc opts :type-str decided-str)]
           (when not-building-permit?
             [target-time-fields (assoc opts :type-str appealed-str)])])))))
